import { Service, signal } from '@angular/core';

export type ToastType = 'confirmed' | 'error' | 'warning';

export interface Toast {
  id: number;
  type: ToastType;
  title: string;
  text?: string;
  expired?: boolean;
}

interface ToastTimer {
  remaining: number;
  startedAt: number;
  handle: ReturnType<typeof setTimeout> | null;
}

@Service()
export class ToasterService {
  private static readonly DurationMs = 3000;

  private nextId = 0;
  private layoutFrozen = false;
  private timers = new Map<number, ToastTimer>();
  readonly toasts = signal<readonly Toast[]>([]);

  confirmed(title: string, text?: string): void {
    this.show('confirmed', title, text);
  }

  error(title: string, text?: string): void {
    this.show('error', title, text);
  }

  warning(title: string, text?: string): void {
    this.show('warning', title, text);
  }

  show(type: ToastType, title: string, text?: string): void {
    const id = ++this.nextId;
    this.toasts.update((toasts) => [...toasts, { id, type, title, text }]);
    this.timers.set(id, {
      remaining: ToasterService.DurationMs,
      startedAt: Date.now(),
      handle: null,
    });
    this.resume(id);
  }

  pause(id: number): void {
    const timer = this.timers.get(id);
    if (!timer || timer.handle === null) return;

    clearTimeout(timer.handle);
    timer.handle = null;
    timer.remaining = Math.max(0, timer.remaining - (Date.now() - timer.startedAt));
  }

  resume(id: number): void {
    const timer = this.timers.get(id);
    if (!timer || timer.handle !== null) return;

    timer.startedAt = Date.now();
    timer.handle = setTimeout(() => this.dismiss(id), timer.remaining);
  }

  setLayoutFrozen(frozen: boolean): void {
    this.layoutFrozen = frozen;
    if (!frozen) {
      this.toasts.update((toasts) => toasts.filter((toast) => !toast.expired));
    }
  }

  dismiss(id: number): void {
    const timer = this.timers.get(id);
    if (timer?.handle != null) clearTimeout(timer.handle);
    this.timers.delete(id);

    this.toasts.update((toasts) =>
      this.layoutFrozen
        ? toasts.map((toast) => (toast.id === id ? { ...toast, expired: true } : toast))
        : toasts.filter((toast) => toast.id !== id),
    );
  }
}
